import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form-latest';
import { Stack } from '@mui/material';
import { Loader, Modal, RichTextInput } from 'melp-design/components';
import { ModalProps } from 'store/modal';
import {
  useAdminTerm,
  useAdminTerms,
  useCreateAdminTerm,
} from 'store/admin-terms';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  content: string;
}

interface Props extends ModalProps {
  termId?: string;
}

export const TermsVersionModal: FC<Props> = ({ closeModal, termId }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const isView = !!termId;
  const seeded = useRef(false);

  const { data: term, isLoading: isLoadingTerm } = useAdminTerm(termId);
  const { data: latestList, isLoading: isLoadingLatestList } = useAdminTerms(
    { page: 1, pageSize: 1 },
    { enabled: !isView },
  );
  const latestId = latestList?.items[0]?.id;
  const { data: latest } = useAdminTerm(isView ? undefined : latestId);
  const { mutate: createTerm, isLoading: isSaving } = useCreateAdminTerm();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: { content: '' },
  });

  useEffect(() => {
    if (isView || seeded.current || isLoadingLatestList || !latestList) {
      return;
    }
    if (latestId && !latest) {
      return;
    }
    reset({ content: latest?.content ?? '' });
    seeded.current = true;
  }, [isView, isLoadingLatestList, latest, latestId, latestList, reset]);

  const isLoading = isView
    ? isLoadingTerm
    : isLoadingLatestList || (!!latestId && !latest);

  const onSubmit = (values: Values) => {
    if (isView) {
      return;
    }
    createTerm(
      { content: values.content },
      {
        onSuccess: () => {
          closeModal();
          predefinedToasts.success.added();
        },
      },
    );
  };

  const title = isView
    ? t('termsAndConditions.version', {
        defaultValue: 'Version {{version}}',
        version: term?.version ?? '',
      })
    : t('termsAndConditions.newVersion', { defaultValue: 'New version' });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={title}
        actions={
          isView
            ? [
                {
                  variant: 'neutral-outline',
                  onClick: () => closeModal(),
                  label: t('common.close'),
                },
              ]
            : [
                {
                  variant: 'neutral-outline',
                  onClick: () => closeModal(),
                  label: t('common.close'),
                },
                {
                  variant: 'primary',
                  label: t('common.save'),
                  type: 'submit',
                  disabled: !isDirty || isSaving || isLoading,
                  loading: isSaving,
                },
              ]
        }
      >
        {isLoading ? (
          <Loader />
        ) : isView ? (
          <RichTextInput value={term?.content ?? ''} displayMode />
        ) : (
          <Stack gap={2}>
            <Controller
              name="content"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('form.required'),
                },
              }}
              render={({ field: { value, onChange, ref }, fieldState }) => (
                <RichTextInput
                  value={value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                  minHeight={240}
                  ref={ref}
                />
              )}
            />
          </Stack>
        )}
      </Modal.Content>
    </form>
  );
};
